import Phaser from "phaser";
import { addBlob, addShadowBlob, sizeBlob } from "../assets/primitives";
import { caveViewForProgress } from "../logic/caveView";
import { approachAlpha, isOnScreen, sceneryLane, shouldRecycle, type ProjectionContext } from "./RunnerProjection";
import { LEAF_FLURRY, motionFx, SKYLINE_DRIFT_PX } from "./motionFx";
import { TrailRibbon } from "./TrailRibbon";
import { setDepthIfChanged } from "../setDepthIfChanged";
import { resetFxSprite, type FxSpritePool } from "./fxPool";

interface WorldProp {
  sprite: Phaser.GameObjects.Image;
  shadow: Phaser.GameObjects.Image;
  distance: number;
  lane: number;
  scaleMul: number;
  kind: "tree" | "near" | "ground";
  flipped: boolean;
  tintShift: number;
}

interface GroundSplat {
  sprite: Phaser.GameObjects.Image;
  distance: number;
  lane: number;
  color: number;
  width: number;
  height: number;
  kind: "grass" | "dirt" | "leaf" | "shadow" | "moss";
  lastColor: number;
  lastAlpha: number;
}

const SPLAT_COUNT = 80;
const SPLAT_SIZE_EPS = 0.75;
/** Above world props/encounters, below HUD (~2000) and the hurt vignette (1900). */
const COMFORT_HOOD_DEPTH = 1850;

const TREE_KEYS = [
  "pine-tree-1",
  "pine-tree-2",
  "pine-tree-3",
  "deciduous-tree-1",
  "deciduous-tree-2",
  "deciduous-tree-3",
];
const NEAR_KEYS = [
  "bush-1",
  "bush-2",
  "bush-3",
  "fern-1",
  "fern-2",
  "fern-3",
  "grass-1",
  "grass-2",
  "wildflowers-1",
  "wildflowers-2",
];
const GROUND_KEYS = [
  "fallen-leaves-1",
  "fallen-leaves-2",
  "mushrooms-1",
  "mushrooms-2",
  "prop-log",
  "root",
  "small-stones-1",
  "small-stones-2",
];

const PROP_TINTS = [0xffffff, 0xf2e6d4, 0xe4eedd, 0xefe4c8, 0xdde8d4, 0xf6edd8];

const GRASS_NEAR = 0x5c3a1e;
const GRASS_FAR = 0xdcc090;
const DIRT_PATCH = 0x7a5c38;
const LEAF_PATCH = 0xb85a28;
const MOSS_PATCH = 0x5a5830;
const SHADOW_PATCH = 0x2a1c10;
const FLOOR_FAR = 0xd2a86e;
const FLOOR_NEAR = 0x4a2a16;
const FLOOR_WASH_FAR = 0xa86c38;
const FLOOR_WASH_NEAR = 0x382010;

export class ForestBackdrop {
  private readonly ground: Phaser.GameObjects.Graphics;
  private readonly trail: TrailRibbon;
  private readonly props: WorldProp[] = [];
  private readonly splats: GroundSplat[] = [];
  private haze: Phaser.GameObjects.Image[] = [];
  private hills: Phaser.GameObjects.Image | null = null;
  private forest: Phaser.GameObjects.Image | null = null;
  private cave: Phaser.GameObjects.Image;
  private caveGlow: Phaser.GameObjects.Image;
  private birds: Phaser.GameObjects.Image;
  private birds2: Phaser.GameObjects.Image | null = null;
  private hood: Phaser.GameObjects.Image[] = [];
  private leafTimer = 0;
  private layerBase = {
    hills: 0,
    forest: 0,
  };
  arrivalT = 0;
  worldScrollMul = 1;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly projection: ProjectionContext,
    private readonly fxSprites: FxSpritePool,
  ) {
    this.paintSky();
    this.placeHorizonLayers();
    this.ground = scene.add.graphics().setDepth(3);
    this.trail = new TrailRibbon(scene, projection);
    this.paintGround();
    this.seedSplats();
    this.updateSplats();
    this.paintHaze();

    const { frame } = projection;
    this.cave = scene.add.image(frame.screenCenterX, frame.horizonY + 20, "cave-mid");
    this.cave.setOrigin(0.5, 1).setDepth(11).setScale(0.12).setAlpha(0.15);
    this.caveGlow = scene.add.image(frame.screenCenterX, frame.horizonY, "cave-warm-glow");
    this.caveGlow.setOrigin(0.5, 0.55).setDepth(12).setScale(0.05).setAlpha(0);

    this.birds = scene.add.image(900, 118, "distant-birds-1");
    this.birds.setDepth(2).setScale(0.52).setAlpha(0.82);
    if (scene.textures.exists("distant-birds-2")) {
      this.birds2 = scene.add.image(340, 104, "distant-birds-2");
      this.birds2.setDepth(2).setScale(0.4).setAlpha(0.68);
    }

    this.seedProps();
    this.placeComfortHood();
  }

  update(elapsedMs: number, progress: number, deltaMs: number, deltaDistance: number, speedT: number): void {
    const scroll = deltaDistance * this.worldScrollMul;
    this.trail.update(scroll);
    this.advanceSplats(scroll);
    this.updateSplats();
    this.updateParallax(elapsedMs);
    this.updateCave(progress);
    this.updateProps(scroll);
    this.spawnLeaves(deltaMs, speedT);
  }

  fadeForeground(duration: number): void {
    if (this.hood.length === 0) {
      return;
    }
    this.scene.tweens.add({
      targets: this.hood,
      alpha: 0,
      duration,
      ease: "Sine.easeIn",
    });
  }

  caveOpening(): { x: number; y: number; width: number; height: number } {
    const width = Math.max(32, this.cave.displayWidth * 0.42);
    const height = Math.max(28, this.cave.displayHeight * 0.38);
    return {
      x: this.cave.x,
      y: this.cave.y - this.cave.displayHeight * 0.48,
      width,
      height,
    };
  }

  layout(): void {
    this.paintGround();
    this.trail.layout();
    this.paintHaze();
    this.placeHorizonLayers(true);
    this.placeComfortHood(true);
  }

  private paintSky(): void {
    const { frame } = this.projection;
    if (this.scene.textures.exists("bg-sky")) {
      const sky = this.scene.add.image(frame.screenCenterX, 0, "bg-sky");
      const cropH = Math.floor(sky.height * 0.48);
      sky.setCrop(0, 0, sky.width, cropH);
      sky.setOrigin(0.5, 0);
      sky.setDisplaySize(frame.width, frame.horizonY + 6);
      sky.setDepth(0);
      return;
    }
    const sky = this.scene.add.graphics().setDepth(0);
    sky.fillGradientStyle(0x8ec8e8, 0x8ec8e8, 0xd7e4c8, 0xcfd8b8, 1);
    sky.fillRect(0, 0, frame.width, frame.horizonY + 8);
  }

  private placeHorizonLayers(reposition = false): void {
    const { frame } = this.projection;
    if (!reposition) {
      this.hills = this.placeWideLayer("bg-distant-hills", frame.horizonY + 4, 1);
      this.forest = this.placeWideLayer("bg-distant-forest", frame.horizonY + 30, 2, 1.02, 0xffffff, 0.96);
    } else {
      this.hills?.setPosition(frame.screenCenterX, frame.horizonY + 4);
      this.forest?.setPosition(frame.screenCenterX, frame.horizonY + 30);
    }
    this.layerBase.hills = this.hills?.x ?? frame.screenCenterX;
    this.layerBase.forest = this.forest?.x ?? frame.screenCenterX;
  }

  private placeWideLayer(
    key: string,
    yBottom: number,
    depth: number,
    scaleMul = 1,
    tint = 0xffffff,
    alpha = 1,
  ): Phaser.GameObjects.Image | null {
    if (!this.scene.textures.exists(key)) {
      return null;
    }
    const { frame } = this.projection;
    const image = this.scene.add.image(frame.screenCenterX, yBottom, key);
    image.setOrigin(0.5, 1).setScale((frame.width / image.width) * scaleMul).setDepth(depth).setAlpha(alpha);
    if (tint !== 0xffffff) {
      image.setTint(tint);
    }
    return image;
  }

  private placeComfortHood(reposition = false): void {
    if (!reposition) {
      this.hood = this.createComfortHood();
    }
    const { frame } = this.projection;
    for (const image of this.hood) {
      this.layoutComfortHood(image, frame.width, frame.height);
    }
  }

  private createComfortHood(): Phaser.GameObjects.Image[] {
    const textures = this.scene.textures;
    const leaves: Phaser.GameObjects.Image[] = [];
    if (textures.exists("bg-foreground-leaves-left")) {
      leaves.push(this.scene.add.image(0, 0, "bg-foreground-leaves-left"));
    }
    if (textures.exists("bg-foreground-leaves-right")) {
      leaves.push(this.scene.add.image(0, 0, "bg-foreground-leaves-right"));
    }
    if (leaves.length > 0) {
      return leaves;
    }
    if (textures.exists("bg-foreground-frame")) {
      return [this.scene.add.image(0, 0, "bg-foreground-frame")];
    }
    return leaves;
  }

  private layoutComfortHood(image: Phaser.GameObjects.Image, width: number, height: number): void {
    image.setDepth(COMFORT_HOOD_DEPTH).setScrollFactor(0);
    const key = image.texture.key;
    if (key === "bg-foreground-frame") {
      image.setOrigin(0.5, 0.5);
      image.setPosition(width * 0.5, height * 0.5);
      image.setDisplaySize(width, height);
      return;
    }
    const fromLeft = key === "bg-foreground-leaves-left";
    image.setOrigin(fromLeft ? 0 : 1, 0.5);
    image.setPosition(fromLeft ? 0 : width, height * 0.5);
    image.setScale(height / Math.max(1, image.height));
  }

  private paintHaze(): void {
    const { frame } = this.projection;
    const cx = frame.screenCenterX;
    const hy = frame.horizonY;
    const specs = [
      { x: cx, y: hy + 10, w: frame.width * 0.36, h: 42, color: 0xf2e8d0, alpha: 0.16 },
      { x: cx - frame.width * 0.16, y: hy + 20, w: 200, h: 34, color: 0xe8dcc0, alpha: 0.1 },
      { x: cx + frame.width * 0.18, y: hy + 16, w: 184, h: 30, color: 0xf4ead4, alpha: 0.08 },
    ];
    if (this.haze.length === 0) {
      this.haze = specs.map((spec) => addBlob(this.scene, spec.x, spec.y, spec.w, spec.h, spec.color, spec.alpha).setDepth(5));
      return;
    }
    this.haze.forEach((image, index) => {
      const spec = specs[index];
      if (!spec) {
        return;
      }
      image.setPosition(spec.x, spec.y);
      sizeBlob(image, spec.w, spec.h);
      image.setTint(spec.color);
      image.setAlpha(spec.alpha);
    });
  }

  private seedSplats(): void {
    for (let i = 0; i < SPLAT_COUNT; i += 1) {
      const side: -1 | 1 = i % 2 === 0 ? -1 : 1;
      const kind: GroundSplat["kind"] =
        i % 11 === 0 ? "shadow" : i % 8 === 0 ? "leaf" : i % 6 === 0 ? "dirt" : i % 5 === 0 ? "moss" : "grass";
      const nearShoulder = i % 5 === 0;
      const sprite = addBlob(this.scene, 0, 0, 8, 4, GRASS_NEAR, 0).setDepth(4);
      sprite.setVisible(false);
      this.splats.push({
        sprite,
        distance: (i * 0.083) % 1,
        lane: side * (kind === "shadow" ? 1.55 + (i % 6) * 0.22 : nearShoulder ? 1.08 + (i % 4) * 0.1 : 1.38 + (i % 11) * 0.2),
        color:
          kind === "leaf"
            ? LEAF_PATCH
            : kind === "dirt"
              ? DIRT_PATCH
              : kind === "moss"
                ? MOSS_PATCH
                : kind === "shadow"
                  ? SHADOW_PATCH
                  : i % 2 === 0
                    ? GRASS_NEAR
                    : GRASS_FAR,
        width: kind === "leaf" ? 0.55 : kind === "shadow" ? 1.8 : 0.85 + (i % 6) * 0.22,
        height: kind === "leaf" ? 0.28 : kind === "shadow" ? 0.55 : 0.38 + (i % 5) * 0.1,
        kind,
        lastColor: -1,
        lastAlpha: -1,
      });
    }
  }

  private advanceSplats(deltaDistance: number): void {
    const { frame } = this.projection;
    for (const splat of this.splats) {
      splat.distance -= deltaDistance;
      if (shouldRecycle(splat.distance, frame) || splat.distance > frame.maxDistance) {
        splat.distance = 0.74 + Math.random() * 0.24;
        const side: -1 | 1 = Math.random() < 0.5 ? -1 : 1;
        splat.lane =
          splat.kind === "shadow"
            ? side * (1.5 + Math.random() * 1.2)
            : side * (Math.random() < 0.28 ? 1.06 + Math.random() * 0.2 : 1.35 + Math.random() * 1.6);
      }
    }
  }

  private paintGround(): void {
    const { frame } = this.projection;
    const g = this.ground;
    g.clear();
    const groundTop = frame.horizonY - 4;
    const groundH = frame.height - groundTop;
    g.fillGradientStyle(FLOOR_FAR, FLOOR_FAR, FLOOR_NEAR, FLOOR_NEAR, 0.5, 0.5, 1, 1);
    g.fillRect(0, groundTop, frame.width, groundH);
    g.fillGradientStyle(FLOOR_WASH_FAR, FLOOR_WASH_FAR, FLOOR_WASH_NEAR, FLOOR_WASH_NEAR, 0.16, 0.16, 0.38, 0.38);
    g.fillRect(0, groundTop + groundH * 0.2, frame.width, groundH * 0.8);
  }

  private updateSplats(): void {
    const { frame } = this.projection;
    for (const splat of this.splats) {
      const pose = this.projection.project(splat.distance, splat.lane);
      const fade = approachAlpha(splat.distance, frame);
      if (fade < 0.05 || pose.y < frame.horizonY - 2 || !isOnScreen(pose, frame, 180)) {
        if (splat.sprite.visible) {
          splat.sprite.setVisible(false);
        }
        continue;
      }
      const farMix = Math.min(1, Math.max(0, splat.distance));
      const farQ = Math.round(farMix * 16) / 16;
      const color =
        splat.kind === "grass" ? mixHex(GRASS_FAR, GRASS_NEAR, 1 - farQ) : splat.kind === "shadow" ? mixHex(SHADOW_PATCH, 0x5a4030, farQ) : splat.color;
      const alpha =
        splat.kind === "shadow"
          ? (0.1 + (1 - farMix) * 0.16) * fade
          : splat.kind === "grass"
            ? (0.22 + (1 - farMix) * 0.34) * fade
            : splat.kind === "leaf"
              ? 0.2 * fade
              : 0.18 * fade;
      const nearShrink = pose.scale <= 0.28 ? 1 : Math.max(0.4, 1 - (pose.scale - 0.28) * 1.25);
      const width = 70 * pose.scale * splat.width * 4.6 * nearShrink;
      const height = 18 * pose.scale * splat.height * 4.2 * nearShrink;
      const sprite = splat.sprite;
      if (!sprite.visible) {
        sprite.setVisible(true);
      }
      sprite.setPosition(pose.x, pose.y);
      if (
        Math.abs(sprite.displayWidth - width) >= SPLAT_SIZE_EPS ||
        Math.abs(sprite.displayHeight - height) >= SPLAT_SIZE_EPS * 0.7
      ) {
        sizeBlob(sprite, width, height);
      }
      if (color !== splat.lastColor) {
        splat.lastColor = color;
        sprite.setTint(color);
      }
      if (Math.abs(alpha - splat.lastAlpha) >= 0.01) {
        splat.lastAlpha = alpha;
        sprite.setAlpha(alpha);
      }
    }
  }

  private updateParallax(elapsedMs: number): void {
    const bob = this.projection.motion.bobY;
    if (this.hills) {
      this.hills.x = this.layerBase.hills + Math.sin(elapsedMs / 9000) * SKYLINE_DRIFT_PX.hills;
      this.hills.y = this.projection.frame.horizonY + 4 + bob * 0.06;
    }
    if (this.forest) {
      this.forest.x = this.layerBase.forest + Math.sin(elapsedMs / 5200 + 0.4) * SKYLINE_DRIFT_PX.forest;
    }
    const birdAmp = SKYLINE_DRIFT_PX.birds;
    const bird2Amp = SKYLINE_DRIFT_PX.birds2;
    this.birds.x = 860 + Math.sin(elapsedMs / 9000) * birdAmp;
    if (this.birds2) {
      this.birds2.x = 340 + Math.sin(elapsedMs / 11000 + 1) * bird2Amp;
    }
  }

  private updateCave(progress: number): void {
    const { frame } = this.projection;
    const view = caveViewForProgress(progress);
    if (this.cave.texture.key !== view.key && this.scene.textures.exists(view.key)) {
      this.cave.setTexture(view.key);
    }
    const pose = this.projection.project(view.distance, 0);
    const plantY = frame.horizonY + view.plantOffsetY;
    this.cave.setPosition(pose.x, plantY);
    this.cave.setOrigin(0.5, 0.92);
    const maxWidth = frame.width * view.widthRatio * (1 + this.arrivalT * 0.55);
    const scale = Math.min(
      pose.scale * (1.15 + progress * 0.55) * (1 + this.arrivalT * 0.4),
      maxWidth / Math.max(1, this.cave.width),
    );
    this.cave.setScale(scale);
    setDepthIfChanged(this.cave, Math.max(6, pose.depth - 24));
    this.cave.setAlpha(Math.min(1, view.alpha + this.arrivalT * 0.25));

    const mouthY = plantY - this.cave.displayHeight * 0.48;
    this.caveGlow.setPosition(pose.x, mouthY);
    this.caveGlow.setScale(scale * 0.35, scale * 0.22);
    setDepthIfChanged(this.caveGlow, this.cave.depth + 1);
    this.caveGlow.setAlpha(progress < 0.2 ? 0 : 0.1 + progress * 0.18);
  }

  private seedProps(): void {
    for (let i = 0; i < 28; i += 1) {
      this.spawnProp("tree", 0.05 + hash01(i, 3) * 0.93);
    }
    for (let i = 0; i < 26; i += 1) {
      this.spawnProp("near", 0.04 + hash01(i, 5) * 0.9);
    }
    for (let i = 0; i < 22; i += 1) {
      this.spawnProp("ground", 0.03 + hash01(i, 7) * 0.88);
    }
  }

  private spawnProp(kind: WorldProp["kind"], distance: number): void {
    const keys = kind === "tree" ? TREE_KEYS : kind === "near" ? NEAR_KEYS : GROUND_KEYS;
    const key = this.pickKey(keys);
    if (!key) {
      return;
    }
    const sprite = this.scene.add.image(0, 0, key);
    sprite.setOrigin(0.5, 1);
    const flipped = Math.random() < 0.5;
    sprite.setFlipX(flipped);
    const shadow = addShadowBlob(this.scene, 0, 0, 64, 18, 0.3);
    const side: -1 | 1 = Math.random() < 0.5 ? -1 : 1;
    this.props.push({
      sprite,
      shadow,
      distance,
      lane: this.laneFor(kind, side),
      scaleMul: this.scaleFor(kind),
      kind,
      flipped,
      tintShift: PROP_TINTS[Math.floor(Math.random() * PROP_TINTS.length)] ?? 0xffffff,
    });
  }

  private updateProps(deltaDistance: number): void {
    const { frame } = this.projection;
    for (const prop of this.props) {
      prop.distance -= deltaDistance * (prop.kind === "near" ? 1.04 : prop.kind === "ground" ? 1 : 0.97);
      const pose = this.projection.project(prop.distance, prop.lane);
      const offNear = pose.perspective > 0.62 && !isOnScreen(pose, frame, 90);
      if (shouldRecycle(prop.distance, frame) || offNear) {
        this.recycleProp(prop);
        continue;
      }
      const hazeTint = pose.closeness < 0.32 ? 0xc5d0b8 : pose.closeness < 0.58 ? 0xe8eedd : prop.tintShift;
      prop.sprite.setOrigin(0.5, 1);
      prop.sprite.setPosition(pose.x, pose.y);
      const size = Math.max(0.05, pose.scale * prop.scaleMul);
      prop.sprite.setScale(size);
      prop.sprite.setFlipX(prop.flipped);
      setDepthIfChanged(prop.sprite, Math.max(12, pose.depth + (prop.kind === "ground" ? -6 : 4)));
      prop.sprite.setAlpha(approachAlpha(prop.distance, frame));
      prop.sprite.setTint(hazeTint);
      prop.sprite.setVisible(pose.closeness > 0 && pose.y < frame.height + 90);
      const showShadow = pose.closeness > 0.08;
      prop.shadow.setVisible(showShadow);
      if (showShadow) {
        prop.shadow.setPosition(pose.x, pose.y + 3);
        const shadowW = prop.kind === "tree" ? 2.6 : prop.kind === "near" ? 1.55 : 1.05;
        sizeBlob(
          prop.shadow,
          64 * pose.scale * shadowW * (0.7 + prop.scaleMul * 0.2),
          18 * pose.scale * (prop.kind === "tree" ? 0.62 : 0.44),
        );
        setDepthIfChanged(prop.shadow, pose.depth - 2);
        prop.shadow.setAlpha((0.14 + pose.closeness * 0.22) * approachAlpha(prop.distance, frame));
      }
    }
  }

  private recycleProp(prop: WorldProp): void {
    const keys = prop.kind === "tree" ? TREE_KEYS : prop.kind === "near" ? NEAR_KEYS : GROUND_KEYS;
    const key = this.pickKey(keys);
    if (key && this.scene.textures.exists(key)) {
      prop.sprite.setTexture(key);
      prop.sprite.setOrigin(0.5, 1);
    }
    const side: -1 | 1 = Math.random() < 0.5 ? -1 : 1;
    prop.flipped = Math.random() < 0.5;
    prop.distance = 0.66 + Math.random() * 0.32 + hash01(prop.sprite.x, 9) * 0.08;
    prop.lane = this.laneFor(prop.kind, side);
    prop.scaleMul = this.scaleFor(prop.kind);
    prop.tintShift = PROP_TINTS[Math.floor(Math.random() * PROP_TINTS.length)] ?? 0xffffff;
  }

  private laneFor(kind: WorldProp["kind"], side: -1 | 1): number {
    const overlapTrail = kind !== "tree" && Math.random() < 0.3;
    if (overlapTrail) {
      return sceneryLane(side, 1.04 + Math.random() * 0.2);
    }
    if (kind === "tree") {
      return sceneryLane(side, 1.55 + Math.random() * 1.35);
    }
    if (kind === "near") {
      return sceneryLane(side, 1.18 + Math.random() * 0.85);
    }
    return sceneryLane(side, 1.1 + Math.random() * 0.7);
  }

  private scaleFor(kind: WorldProp["kind"]): number {
    if (kind === "tree") {
      return 1.25 + Math.random() * 0.85;
    }
    if (kind === "near") {
      return 0.7 + Math.random() * 0.55;
    }
    return 0.42 + Math.random() * 0.38;
  }

  private spawnLeaves(deltaMs: number, speedT: number): void {
    this.leafTimer += deltaMs;
    const fx = motionFx();
    const interval = LEAF_FLURRY.intervalMs - speedT * LEAF_FLURRY.speedCutMs;
    if (!this.fxSprites.alive || !fx.leafBurst || this.leafTimer < interval) {
      return;
    }
    this.leafTimer = 0;
    const useAnim = this.scene.anims.exists("floating-leaf-particles");
    const side = Math.random() < 0.5 ? -1 : 1;
    const start = this.projection.project(0.22 + Math.random() * 0.24, side * (0.9 + Math.random() * 1.2));
    const key = useAnim
      ? "floating-leaf-particles"
      : Math.random() < 0.5
        ? "fallen-leaves-1"
        : "fallen-leaves-2";
    const leaf = this.fxSprites.acquire();
    resetFxSprite(leaf, key);
    leaf.setPosition(start.x, start.y);
    leaf.setDepth(Math.max(20, start.depth + 8)).setScale(start.scale * 0.7).setAlpha(0.9);
    if (useAnim) {
      leaf.play("floating-leaf-particles");
    }
    this.scene.tweens.add({
      targets: leaf,
      x: start.x + side * (40 + Math.random() * 60),
      y: start.y + 90 + Math.random() * 50,
      scale: start.scale * LEAF_FLURRY.scaleMul,
      alpha: 0,
      duration: 1800 + Math.random() * 600,
      onComplete: () => this.fxSprites.release(leaf),
    });
  }

  private pickKey(keys: string[]): string | null {
    const available = keys.filter((key) => this.scene.textures.exists(key));
    if (available.length === 0) {
      return null;
    }
    return available[Math.floor(Math.random() * available.length)] ?? null;
  }
}

function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

function hash01(value: number, salt: number): number {
  let n = Math.imul(Math.floor(value) + 1, 374761393) + Math.imul(salt + 1, 668265263);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return (n >>> 0) / 4294967295;
}
